import React, { useState, useCallback, useEffect } from 'react';
import UserDropdown from '../ui/UserDropdown';

interface TopBarProps {
  userEmail?: string;
  className?: string;
  currentApp?: string;
  onLogout?: () => void;
  orgId?: string;
  isMobile?: boolean;
  onToggleSidebar?: () => void;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  showUserAvatar?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({ userEmail, className = '', currentApp, onLogout, orgId = 'default', isMobile = false, onToggleSidebar, leftContent, rightContent, showUserAvatar = true }) => {
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);


  const getUserInitials = (email: string | undefined): string => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split('.');
    return parts.map(part => part[0]?.toUpperCase() || '').join('').slice(0, 2) || 'U';
  };


  // Handle search submission
  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search results or perform search action
      // For now, just log the search query
      console.log('Search query:', searchQuery);
      // TODO: Implement search functionality
    }
  }, [searchQuery]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserDropdown && !(event.target as Element).closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowUserDropdown(false);
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className={`bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 ${className}`}>
      <div className="flex items-center justify-between">
        {/* Left Side - Mobile Menu Button */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Mobile Menu Button */}
          {isMobile && onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-44 min-w-44"
              title="Open menu"
              aria-label="Open menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          {leftContent}
        </div>

        {/* Center - Search Bar */}
        <div className="flex-1 flex justify-center">
          <form onSubmit={handleSearchSubmit} className="relative w-full max-w-md">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={isMobile ? "Search..." : "Search across your organization..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className={`block w-full pl-8 sm:pl-10 pr-3 py-2 border rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm transition-all duration-200 ${
                  isSearchFocused ? 'border-blue-500 shadow-sm' : 'border-gray-300'
                }`}
                aria-label="Search"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center min-h-44 min-w-44"
                  aria-label="Clear search"
                >
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </form>
        </div>
        
        {/* Right Side - Custom Content and User Avatar */}
        <div className="flex items-center space-x-1 sm:space-x-3">
          {/* Custom Right Content */}
          {rightContent}
          
          {/* User Avatar - Always visible but smaller on mobile */}
          {showUserAvatar && (
          <div className="relative user-dropdown">
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="w-7 h-7 sm:w-8 sm:h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-medium hover:bg-purple-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 min-h-44 min-w-44"
              title={userEmail || 'User'}
              aria-label="User menu"
              aria-expanded={showUserDropdown}
            >
              {getUserInitials(userEmail)}
            </button>
            
            {/* User Dropdown */}
            <UserDropdown
              isOpen={showUserDropdown}
              onClose={() => setShowUserDropdown(false)}
              userEmail={userEmail}
              onLogout={onLogout}
              orgId={orgId}
            />
          </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default TopBar;
